import React, { useEffect, useState } from 'react';
import { Box, Flex, Drawer, Typography, TextInput, Button, IconButton, color, Card } from '@kistech/kis-ui';
import { Box as MuiBox, Typography as MuiTypography, Grid } from '@mui/material';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { useSelector } from 'react-redux';
import { actions as kioskActions, selectors as kioskSelectors, Kiosk } from 'store/kiosks';
import { useStyles } from '../styled';

export interface KioskRestockDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  kiosk: Kiosk;
}

export const KioskRestockDrawer = (props: KioskRestockDrawerProps) => {
  const { isOpen, onClose, kiosk } = props;

  const { updateKiosk, logEvent, resetAction } = useActions({
    updateKiosk: kioskActions.updateKiosk,
    logEvent: kioskActions.logEvent,
    resetAction: kioskActions.resetAction
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const { success } = useSelector(kioskSelectors.selectAction);

  const [stockValues, setStockValues] = useState<{ [key: string]: number }>({});

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (isOpen && success) {
      onClose();
      resetAction();
    }
    setStockValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, success]);

  const validateAndSetStockValues = (value: string, kioskHardwareId: string) => {
    if (value && !isNaN(Number(value)) && Number(value) >= 0) {
      setStockValues((prevState) => ({
        ...prevState,
        [kioskHardwareId]: Math.abs(Number(value))
      }));
    } else if (!value) {
      setStockValues((prevState) => {
        const { [kioskHardwareId]: _, ...rest } = prevState;
        return rest;
      });
    }
  };

  // on click data entry overlay
  const onClickSave = () => {
    updateKiosk({
      id: kiosk.id,
      hardware: Object.entries(stockValues).map(([kioskHardwareId, currentStock]) => ({
        id: kioskHardwareId,
        values: { currentStock }
      }))
    });

    // kioskId needs to be replaced with exact data.
    logEvent({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} hasBorderRadius>
        <Grid container className={classes.kioskRestockDetailsOverlay}>
          <Card fullHeight>
            <Grid className={classes.kioskOverlayHeaderBlock}>
              <Grid className={classes.kioskOverlayHeading}>
                <Flex>
                  <MuiTypography className={classes.kioskRestockHeading} style={{ fontWeight: 600 }}>
                    Load Ticket Stock
                  </MuiTypography>
                </Flex>
              </Grid>
              <Grid className={classes.kioskOverlayCloseIcon}>
                <IconButton
                  name="exit_circle"
                  appearance="transparent"
                  size={40}
                  onClick={onClose}
                  data-testid="kiosk-restock-x"
                />
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography weight={600} size={16}>
                {kiosk.name}
              </Typography>
            </Box>
            <Card.Divider color={color.BORDER} />
            <MuiBox className={classes.kioskRestockBox}>
              {kiosk.hardware
                .filter((h) => h.type === 'printer')
                .map((printer, idx) => (
                  <Flex justify="between" padding="0 0 6 0" width="350px" key={`printer-restock-${idx}`}>
                    <Flex align="center">
                      <MuiTypography className={classes.restockPrinterLabel}>
                        {printer.configuration.displayName || ''} Printer
                      </MuiTypography>
                    </Flex>
                    <Flex align="center" height="50px">
                      <TextInput
                        type="text"
                        size="medium"
                        align="center"
                        inputStyle={{ width: '150px', minWidth: '150px', height: '50px' }}
                        placeholder={(printer.values.currentStock || 0).toString()}
                        value={stockValues.hasOwnProperty(printer.id) ? stockValues[printer.id] : ''}
                        onChange={(e) => validateAndSetStockValues(e.target.value, printer.id)}
                        data-testid={`restock-input-${idx}`}
                      />
                    </Flex>
                  </Flex>
                ))}
              {/* Save button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button
                  size="small"
                  appearance="primary"
                  onClick={onClickSave}
                  className={classes.saveButton}
                  isDisabled={Object.keys(stockValues).length === 0}
                >
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
